import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Lock, ShieldCheck, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [backendUp, setBackendUp] = useState(true);
  const nav = useNavigate();

  useEffect(() => {
    api.get("/health").then(() => setBackendUp(true)).catch(() => setBackendUp(false));
  }, []);

  useEffect(() => {
    if (user && user.is_admin) nav("/admin", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setSubmitting(true);
    const res = await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success("Welcome back, Admin.");
      nav("/admin", { replace: true });
    } else {
      setErr(res.error || "Login failed");
    }
  };

  return (
    <div className="theme-admin min-h-screen bg-[color:var(--admin-bg)] text-[color:var(--admin-ink)] flex items-center justify-center px-4"
         data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="inline-flex items-center gap-2 text-slate-400 hover:text-slate-200 text-sm mb-8"
              data-testid="admin-login-back">
          <ArrowLeft className="h-4 w-4" /> Back to store
        </Link>

        <div className="bg-[color:var(--admin-surface)] border border-white/5 rounded-xl p-8 shadow-2xl">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 rounded-md bg-amber-500/10 text-amber-400">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <div className="overline text-amber-400">Admin Portal</div>
              <div className="font-serif-display text-2xl">ATSHREE Dashboard</div>
            </div>
          </div>

          {!backendUp && (
            <div className="mb-4 rounded-md border border-red-500/40 bg-red-500/10 text-red-300 p-3 text-sm"
                 data-testid="backend-down-banner">
              Backend unreachable — please check the server. This is not a login failure.
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Email</Label>
              <Input
                type="email" required autoFocus autoComplete="username"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="mt-1.5 bg-slate-900/60 border-white/10 text-slate-100 h-11"
                data-testid="admin-login-email"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-widest">Password</Label>
              <Input
                type="password" required autoComplete="current-password"
                value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1.5 bg-slate-900/60 border-white/10 text-slate-100 h-11"
                data-testid="admin-login-password"
              />
            </div>
            {err && (
              <div className="text-red-400 text-sm" data-testid="admin-login-error">{err}</div>
            )}
            <Button
              type="submit" disabled={submitting || !backendUp}
              className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold"
              data-testid="admin-login-submit"
            >
              <Lock className="h-4 w-4 mr-2" />
              {submitting ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 text-xs text-slate-500 font-mono-accent">
            Secure JWT · single-role admin · session 8h
          </div>
        </div>
      </div>
    </div>
  );
}
